// @flow

import i18n from 'app/common/lib/i18n';
import { dangerouslyFormat } from 'app/common/lib/StringUtil';
import { GRID_ITEM_WIDTH } from 'app/common/lib/react/gridUtils';
import { type UnauthSeoExperiments } from 'app/common/lib/react/withUnauthSeoExperiments';
import type { PinType, ContextType } from 'app/common/react/components/growth/unauth/lib/PropTypes';
import { smartTruncate, removeMultipleSpaces } from 'app/common/lib/react/strUtils';

// These methods determine what segments of the pin  (e.g. description, see more button, carousel, attribution)
// are displayed on the pin and the content of each segment.  They are used in two places. 1) React, to render
// the pin and 2) pingridCSS.js, to measure the height of each pin.  If you need to change an element
// of the pin do it here, so that it's used by both rendering and server measurement

const descriptionMaxLength = 496;
const tallTitleLineHeight = 23;
const normalTitleLineHeight = 19;
const SIMILAR_PINS_NUMBER = 6;

const BOARD_TITLE_CHARATER_NUMBER = 35;
const BOARD_TITLE_HEIGHT = 12;
const VASE_DESCRIPTION_HEIGHT = 11;
const VASE_DESCRIPTION_CHARACTER_NUMBER = 49;
const GAP_MARGIN_PIN_HEIGHT = 10;

const DESKTOP_GIRAFFE_IMAGE_CLIP_HEIGHT = 500;
const DESKTOP_GIRAFFE_IMAGE_CLIP_HEIGHT_THRESHOLD = 600;
const VASE_DESCRIPTION_GAP_HEIGHT = 5;
const PINNER_IMAGE_SIZE = 24;
const PINNER_ATTRIBUTION_MARGIN_BOTTOM = 4;

export const getUserFullName = (board: Object, pinner: Object) => {
  const username = board && board.url ? board.url.split('/')[1] : '';
  let userFullName = username;
  if (pinner && pinner.full_name) {
    userFullName = pinner.full_name;
  }
  return userFullName;
};

// Pin utils
export const getPinMeasurements = (seoUnauthExperiments: ?UnauthSeoExperiments = null) => {
  const seeMoreButtonHeight = 15;
  const seeMoreButtonBottomMargin = 3;
  const vaseCarouselHeight = 30;
  const descriptionLineHeight = 17;
  const descriptionVerticalMargin = 10;
  const maxNumTitleLines = 1;
  const maxNumDescriptionLines = 2;

  return {
    seeMoreButtonHeight,
    seeMoreButtonBottomMargin,
    vaseCarouselHeight,
    descriptionLineHeight,
    descriptionVerticalMargin,
    maxNumTitleLines,
    maxNumDescriptionLines,
  };
};

export const getRecipeMeasurements = () => {
  const recipeImageWidth = 418;
  const expandButtonHeight = 36;
  const expandButtonTopMargin = 10;
  const moreInformationHeight = 82;

  return {
    recipeImageWidth,
    expandButtonHeight,
    expandButtonTopMargin,
    moreInformationHeight,
  };
};

export const getTitleLineHeight = (locale: string) => {
  if (locale === 'ja') {
    return tallTitleLineHeight;
  } else {
    return normalTitleLineHeight;
  }
};

export const getTitleMaxHeight = (locale: string, seoUnauthExperiments: UnauthSeoExperiments) => {
  const lineHeight = getTitleLineHeight(locale);
  const maxNumTitleLines = getPinMeasurements(seoUnauthExperiments).maxNumTitleLines;
  return lineHeight * maxNumTitleLines;
};

// PinIconAndAttribution Utils
export const processRichSummary = (pin: PinType) => {
  const { rich_summary } = pin;
  let { apple_touch_icon_images: touchIcons, favicon_images: favicons } = rich_summary;
  touchIcons = touchIcons || {};
  favicons = favicons || {};

  return {
    faviconLink: touchIcons['50x'] || favicons['50x'] || touchIcons.orig || favicons.orig,
    siteName: rich_summary.site_name || pin.domain,
    title: rich_summary.display_name,
  };
};

export const processPlaceInfo = (pin: PinType) => {
  const { place_summary } = pin;
  return {
    faviconLink: place_summary.source_icon,
    siteName: place_summary.source_name || pin.domain,
    title: place_summary.name,
  };
};

const detectAvailable = (pin: PinType) => {
  const { place_summary: placeSummary, rich_summary: richSummary } = pin;
  const hasRichSummary = richSummary && richSummary.type_name !== 'mobile application';
  const hasRichAttributionTitle = hasRichSummary && richSummary.display_name;
  const hasPlaceInfo = !!placeSummary;
  return {
    hasRichSummary,
    hasRichAttributionTitle,
    hasPlaceInfo,
  };
};

export const calculatePinAttributionTitleNumLines = (
  title: string,
  locale: string,
  seoUnauthExperiments: UnauthSeoExperiments,
) => {
  const normalLineNumberCharacters = 27;
  const shortLineNumberCharacters = 14;
  let numLinesForTitle = 0;
  if (title) {
    let lineLength = normalLineNumberCharacters;
    if (locale === 'ja') {
      lineLength = shortLineNumberCharacters;
    }
    const maxNumberLines = getPinMeasurements(seoUnauthExperiments).maxNumTitleLines;
    numLinesForTitle = Math.floor(title.length / lineLength) + 1;
    if (numLinesForTitle > maxNumberLines) {
      numLinesForTitle = maxNumberLines;
    }
  }
  return numLinesForTitle;
};

const getTitleHeight = (
  title: ?string,
  locale: string,
  seoUnauthExperiments: UnauthSeoExperiments,
) => {
  if (title) {
    const numLinesForTitle = calculatePinAttributionTitleNumLines(
      title,
      locale,
      seoUnauthExperiments,
    );
    const titleLineHeight = getTitleLineHeight(locale);
    return titleLineHeight * numLinesForTitle;
  } else {
    return 0;
  }
};

export const getDescriptiveH1TitleText = (visualAnnotations: Array<string>) => {
  let headerText = null;
  let primaryAnnotationText = null;
  if (visualAnnotations !== undefined && visualAnnotations.length > 0) {
    const primaryAnnotation = visualAnnotations[0];
    headerText = dangerouslyFormat(i18n._('Explore {{ primaryAnnotation }} and more!'), {
      primaryAnnotation,
    });
    primaryAnnotationText = headerText;

    let showSecondaryAnnotation = true;
    // todo (sophia) - ugly way of dealing with near dups in visual annotations
    visualAnnotations.forEach((annotation) => {
      if (
        annotation.toLowerCase().replace(/ /g, '') !==
          primaryAnnotation.toLowerCase().replace(/ /g, '') &&
        showSecondaryAnnotation
      ) {
        headerText = dangerouslyFormat(
          i18n._('Explore {{ primaryAnnotation }}, {{ secondaryAnnotation }}, and more!'),
          { primaryAnnotation, secondaryAnnotation: annotation },
        );
        showSecondaryAnnotation = false;
      }
    });
    if (headerText && headerText.length < 60) {
      return headerText;
    } else if (primaryAnnotationText && primaryAnnotationText.length < 60) {
      return primaryAnnotationText;
    }
  }
  return null;
};

export const getAttributionTitleText = (pin: PinType) => {
  const { hasRichAttributionTitle, hasPlaceInfo } = detectAvailable(pin);
  if (hasRichAttributionTitle) {
    return processRichSummary(pin).title;
  } else if (hasPlaceInfo) {
    return processPlaceInfo(pin).title;
  } else if (pin.rich_metadata && pin.rich_metadata.title) {
    return pin.rich_metadata.title;
  }
  return null;
};

const hasRichAttributionTitleAndPlaceInfo = (pin: PinType) => {
  const rich_summary = pin.rich_summary;
  const hasRichSummary = rich_summary && rich_summary.type_name !== 'mobile application';
  const hasRichAttributionTitle = hasRichSummary && rich_summary.display_name;
  const hasPlaceInfo = !!pin.place_summary;
  return { hasRichSummary, hasRichAttributionTitle, hasPlaceInfo };
};

export const calculateAttributionTitleHeight = (
  pin: PinType,
  context: ContextType,
  seoUnauthExperiments: UnauthSeoExperiments,
) => {
  const title = getAttributionTitleText(pin);
  const titleHeight = getTitleHeight(title, context.locale, seoUnauthExperiments);
  const margin = 8;
  return titleHeight + margin;
};

const fetchDescriptions = (pinJoin: {
  visual_descriptions?: Array<string>,
  i18n_visual_descriptions?: Array<string>,
}) => {
  if (pinJoin.visual_descriptions && pinJoin.visual_descriptions.length > 0) {
    return pinJoin.visual_descriptions;
  } else if (pinJoin.i18n_visual_descriptions && pinJoin.i18n_visual_descriptions.length > 0) {
    return pinJoin.i18n_visual_descriptions;
  }
  return undefined;
};

export const shouldRenderAttributionTitle = (pin: PinType) => {
  const { hasRichAttributionTitle, hasPlaceInfo } = hasRichAttributionTitleAndPlaceInfo(pin);
  if (hasRichAttributionTitle || hasPlaceInfo) {
    return true;
  }
  if (pin.rich_metadata && pin.rich_metadata.title) {
    return true;
  }
  return false;
};

const computeTitle = (pin: PinType) =>
  (pin.rich_summary && pin.rich_summary.display_name) ||
  (pin.place_summary && pin.place_summary.name) ||
  '';

export const getDescriptionAndAnnotationsExp = (pin: PinType) => {
  const pinJoin = pin.pin_join || {};
  let vaseAnnotations = pinJoin.visual_annotation || [];
  let vaseDescriptions = fetchDescriptions(pinJoin) || [];
  let description = pin.description || '';
  const metaDescription = (pin.rich_metadata || {}).description || '';
  const annotationsWithLinks = vaseAnnotations.map((annotation) => {
    const annotationLinkMap = pinJoin.annotations_with_links || {};
    const annotationWithLink = annotationLinkMap[annotation] || {
      name: annotation,
      url: '',
    };
    return annotationWithLink;
  });

  const titleLower = computeTitle(pin)
    .toLocaleLowerCase()
    .trim();
  const descriptionLower = description.toLocaleLowerCase().trim();
  const metaDescriptionLower = metaDescription.toLocaleLowerCase().trim();
  const firstVaseDescriptionLower =
    vaseDescriptions.length > 0 ? vaseDescriptions[0].toLocaleLowerCase().trim() : '';

  if (metaDescription && metaDescription.length > 3 && metaDescriptionLower !== titleLower) {
    if (
      descriptionLower !== metaDescriptionLower &&
      descriptionLower !== firstVaseDescriptionLower
    ) {
      vaseDescriptions = [...vaseDescriptions, description];
    }
    description = metaDescription;
  } else if (
    vaseDescriptions.length > 0 &&
    vaseDescriptions[0].length >= 3 &&
    firstVaseDescriptionLower !== titleLower
  ) {
    // Pin description quality tends to be worse than vase descriptions, so we use the first vase description as the pin
    // description.
    if (
      description &&
      description.length > 3 &&
      descriptionLower !== titleLower &&
      descriptionLower !== firstVaseDescriptionLower
    ) {
      // If description is not too short or redundant, add it to vase description.
      vaseDescriptions = [...vaseDescriptions, description];
    }
    description = vaseDescriptions[0];

    vaseDescriptions.shift();
  } else if (!description || description.length < 3 || descriptionLower === titleLower) {
    // Pin description is too short or redundant and we don't have vase descriptions to use instead.
    if (vaseAnnotations.length) {
      description = vaseAnnotations.join(', ');
      vaseAnnotations = [];
    } else {
      description = '';
    }
  }

  if (description) {
    description = removeMultipleSpaces(smartTruncate(description, descriptionMaxLength)).trim();
  }

  return { description, vaseAnnotations, vaseDescriptions, annotationsWithLinks };
};

export const getDescriptionAndAnnotationsCur = (pin: PinType) => {
  const pinJoin = pin.pin_join || {};
  let vaseAnnotations = pinJoin.visual_annotation || [];
  let vaseDescriptions = fetchDescriptions(pinJoin) || [];
  let description = pin.description || '';
  const annotationsWithLinks = vaseAnnotations.map((annotation) => {
    const annotationLinkMap = pinJoin.annotations_with_links || {};
    const annotationWithLink = annotationLinkMap[annotation] || {
      name: annotation,
      url: '',
    };
    return annotationWithLink;
  });

  const titleLower = computeTitle(pin)
    .toLocaleLowerCase()
    .trim();

  const descriptionLower = description.toLocaleLowerCase().trim();

  const firstVaseDescriptionEqualsTitle =
    vaseDescriptions.length > 0 && vaseDescriptions[0].toLocaleLowerCase().trim() === titleLower;
  const descriptionEqualsTitle = descriptionLower === titleLower;
  if (
    vaseDescriptions.length > 0 &&
    vaseDescriptions[0].length >= 3 &&
    !firstVaseDescriptionEqualsTitle
  ) {
    // Pin description quality tends to be worse than vase descriptions, so we use the first vase description as the pin
    // description.
    if (!description || description.length < 3 || descriptionEqualsTitle) {
      // If description is too short or redundant, don't keep.
      description = vaseDescriptions[0];
      // do not shift() vaseDescriptions since that mutates props
      // $FlowFixMe: Missing type annotation for T.
      vaseDescriptions = vaseDescriptions.slice(1);
    } else {
      // Otherwise move description to last vase description.
      vaseDescriptions = [...vaseDescriptions, description];
      // we can shift off of vaseDescriptions here b/c we set it on the previous line
      description = vaseDescriptions.shift();
    }
  } else if (!description || description.length < 3 || descriptionEqualsTitle) {
    // Pin description is too short or redundant and we don't have vase descriptions to use instead.
    if (vaseAnnotations.length) {
      description = vaseAnnotations.join(', ');
      vaseAnnotations = [];
    } else {
      description = '';
    }
  }

  if (description) {
    description = removeMultipleSpaces(smartTruncate(description, descriptionMaxLength)).trim();
  }

  return { description, vaseAnnotations, vaseDescriptions, annotationsWithLinks };
};

export const getDescriptionAndAnnotations = (
  pin: PinType,
  seoUnauthExperiments?: UnauthSeoExperiments,
  pageType?: string,
) => {
  if (pageType === 'pin') {
    return getDescriptionAndAnnotationsExp(pin);
  }
  if (pageType === 'board') {
    const metaTitleDescriptionGroup = seoUnauthExperiments
      ? seoUnauthExperiments.v2ActivateExperiment('meta_pin_title_description_board') || ''
      : '';
    if (metaTitleDescriptionGroup.startsWith('enabled')) {
      return getDescriptionAndAnnotationsExp(pin);
    }
  }
  return getDescriptionAndAnnotationsCur(pin);
};

export const shouldUseVaseCarousel = (
  context: ContextType,
  vaseAnnotations: ?Array<string>,
  seoUnauthExperiments: ?UnauthSeoExperiments,
  isPaidTraffic: boolean = false,
) => vaseAnnotations && vaseAnnotations.length > 0 && !context.userAgent.isMobile && !isPaidTraffic;

export const calculateDescriptionNumLines = (
  description: string,
  seoUnauthExperiments: UnauthSeoExperiments,
) => {
  let numLinesForDescription = 0;
  if (description) {
    description = removeMultipleSpaces(description);
    const lineLength = 35;
    const maxNumberLines = getPinMeasurements(seoUnauthExperiments).maxNumDescriptionLines;
    numLinesForDescription = Math.floor(description.length / lineLength) + 1;
    if (numLinesForDescription > maxNumberLines) {
      numLinesForDescription = maxNumberLines;
    }
  }
  return numLinesForDescription;
};

export const getDescriptionMaxHeight = (
  locale: string,
  seoUnauthExperiments: UnauthSeoExperiments,
) => {
  const pinMeasurements = getPinMeasurements(seoUnauthExperiments);
  const lineHeight = pinMeasurements.descriptionLineHeight;
  const maxNumTitleLines = pinMeasurements.maxNumDescriptionLines;
  return lineHeight * maxNumTitleLines;
};

export const shouldRenderSeeMore = (
  vaseDescriptions: Array<string>,
  description: string,
  vaseAnnotations: ?Array<string>,
  pin: PinType,
  context: ContextType,
  seoUnauthExperiments: UnauthSeoExperiments,
  renderSeeMore: boolean = true,
  secondaryFeed: boolean = false,
) => {
  if (!pin) {
    return null;
  }
  // return false if feature specifies to hide see more block
  if (renderSeeMore) {
    // if there is no hidden markup, don't show the 'See More' link
    const useVaseCarousel = shouldUseVaseCarousel(context, vaseAnnotations, seoUnauthExperiments);
    const shouldRender =
      (vaseDescriptions && vaseDescriptions.length > 0) ||
      (context.userAgent.isMobile && description) ||
      (!useVaseCarousel && vaseAnnotations && vaseAnnotations.length > 0);
    return shouldRender;
  } else {
    return false;
  }
};

// Find out board attribution
export const calculateBoardAttributionHeight = (pin: PinType) => {
  // Find out .... attribution
  const board = pin.board;
  let boardAttributionHeight = 0;
  if (board && board.url && board.name) {
    const username = board.url.split('/')[1];
    let userFullName = username || '';
    if (pin.pinner && pin.pinner.full_name) {
      userFullName = pin.pinner.full_name;
    }
    boardAttributionHeight = Math.ceil(
      ((BOARD_TITLE_CHARATER_NUMBER + board.name.length + userFullName.length) /
        VASE_DESCRIPTION_CHARACTER_NUMBER) *
        BOARD_TITLE_HEIGHT,
    );
  }
  return boardAttributionHeight;
};

// Refer to style pinnerImage and pinnerDescription in
// webapp/app/common/react/components/growth/unauth/pin/PinDescription/PinDescription.js
export const calculatePinnerBoardAttributionHeight = () =>
  PINNER_IMAGE_SIZE + PINNER_ATTRIBUTION_MARGIN_BOTTOM;

// Calculate the vase descriptions height
export const getVaseDescriptionHeight = (
  pin: PinType,
  seoUnauthExperiments?: UnauthSeoExperiments,
  pageType?: string,
) => {
  const { vaseDescriptions } = getDescriptionAndAnnotations(pin, seoUnauthExperiments, pageType);

  // vase description
  let pinVaseDescriptionMarkupHeightLines = 0;
  if (vaseDescriptions && vaseDescriptions.length > 0) {
    vaseDescriptions.forEach((text, index) => {
      pinVaseDescriptionMarkupHeightLines += Math.ceil(
        text.length / VASE_DESCRIPTION_CHARACTER_NUMBER,
      );
    });
  }
  const vaseDescriptionHeight = pinVaseDescriptionMarkupHeightLines * VASE_DESCRIPTION_HEIGHT;
  const vaseDescriptionGapHeight = vaseDescriptions.length * VASE_DESCRIPTION_GAP_HEIGHT;
  return vaseDescriptionHeight + vaseDescriptionGapHeight;
};

export const calculatePinDescriptionHeight = (
  pin: PinType,
  context: ContextType,
  seoUnauthExperiments: UnauthSeoExperiments,
  isPaidTraffic: boolean,
  pageType: string = '',
) => {
  let height = 0;
  const { description, vaseAnnotations, vaseDescriptions } = getDescriptionAndAnnotations(
    pin,
    seoUnauthExperiments,
    context.pageType,
  );
  const pinMeasurements = getPinMeasurements(seoUnauthExperiments);
  if (
    shouldRenderSeeMore(
      vaseDescriptions,
      description,
      vaseAnnotations,
      pin,
      context,
      seoUnauthExperiments,
    )
  ) {
    height += pinMeasurements.seeMoreButtonHeight;
    height += pinMeasurements.seeMoreButtonBottomMargin;
  }

  if (shouldUseVaseCarousel(context, vaseAnnotations, seoUnauthExperiments, isPaidTraffic)) {
    height += pinMeasurements.vaseCarouselHeight;
  }
  if (!isPaidTraffic) {
    const numLinesForDescription = calculateDescriptionNumLines(description, seoUnauthExperiments);
    height +=
      pinMeasurements.descriptionVerticalMargin +
      pinMeasurements.descriptionLineHeight * numLinesForDescription;
  }

  // Pinner image and name
  if (pin.pinner && pin.board) {
    height += calculatePinnerBoardAttributionHeight();
  }

  // Refer to webapp/app/common/react/components/growth/unauth/pin/pinUtils.js
  // show_pin_description_pin_page experiment
  if (pin.expandedVASE) {
    // Vase description
    if (vaseDescriptions && vaseDescriptions.length > 0) {
      const vaseDescriptionHeight = getVaseDescriptionHeight(
        pin,
        seoUnauthExperiments,
        context.pageType,
      );
      height += vaseDescriptionHeight;
    }
  }

  return height;
};

export const calculatePinMetaHeight = (
  pin: PinType,
  context: ContextType,
  seoUnauthExperiments: UnauthSeoExperiments,
  hideMeta: boolean = false,
  isPaidTraffic: boolean,
  pageType: string = '',
) => {
  const margin = GAP_MARGIN_PIN_HEIGHT;

  // Refer to webapp/app/common/react/components/growth/unauth/pin/PinMeta/PinMeta.js
  // hideMeta is always false
  const attributionTitleHeight = shouldRenderAttributionTitle(pin)
    ? calculateAttributionTitleHeight(pin, context, seoUnauthExperiments)
    : 0;
  const descriptionHeight = !hideMeta
    ? calculatePinDescriptionHeight(pin, context, seoUnauthExperiments, isPaidTraffic, pageType)
    : 0;

  if (attributionTitleHeight || descriptionHeight) {
    return descriptionHeight + attributionTitleHeight + margin;
  }

  // no added margin if no meta shown
  return 0;
};

export const getPinImage = (
  pin: PinType,
  imageSize: string = 'orig',
): {| url: string, width: number, height: number |} => {
  // Make sure image exists in pin object, and if not fall-back to next smaller image that exists
  const imageSizes = ['orig', '736x', '564x', '474x', '236x', '170x'];
  let image = pin.images[imageSize];
  if (image) {
    return image;
  } else {
    const imageSizesIndex = imageSizes.indexOf(imageSize);
    if (imageSizesIndex >= 0) {
      for (let i = imageSizesIndex + 1; i < imageSizes.length; i += 1) {
        const thisImageSize = imageSizes[i];
        image = pin.images[thisImageSize];
        if (image) {
          return image;
        }
      }
    }
  }
  return pin.images['236x'];
};

export function getPinGifOrImageSrc(pin: *): string {
  const hasGif = pin.embed && pin.embed.type === 'gif';
  if (hasGif) {
    return pin.embed.src;
  }

  const hasImages = pin.images;
  if (hasImages) {
    const pinImages = pin.images;

    const pinImageDimensions = Object.keys(pinImages).map((dim) => {
      const formattedDim = dim.split('x')[0];

      if (Number.isNaN(Number(formattedDim))) {
        return dim;
      }

      pinImages[formattedDim] = pinImages[dim];
      return formattedDim;
    });

    pinImageDimensions.sort();

    const bestDimension = pinImageDimensions.pop();
    return pinImages[bestDimension].url;
  }

  return pin.image_large_url || '';
}

// Pin Image Utils
export const calculatePinImageHeight = (pin: PinType, maxImageHeight: number) => {
  if (!pin || !pin.images || !pin.images['236x']) {
    return 0;
  }
  const imageHeight = pin.images['236x'].height;
  let pinImageHeight = imageHeight < maxImageHeight ? imageHeight : maxImageHeight;

  // Testing a more accurate image height calculation method based on PinImage.js image
  // height calculation
  let pinImageCropHeight = DESKTOP_GIRAFFE_IMAGE_CLIP_HEIGHT_THRESHOLD;
  if (imageHeight > DESKTOP_GIRAFFE_IMAGE_CLIP_HEIGHT_THRESHOLD && !pin.fullImage) {
    pinImageCropHeight = DESKTOP_GIRAFFE_IMAGE_CLIP_HEIGHT;
  }
  const image = getPinImage(pin, 'orig');
  if (image) {
    const imageHeight = Math.floor((image.height * GRID_ITEM_WIDTH) / image.width);
    pinImageHeight = imageHeight > pinImageCropHeight ? pinImageCropHeight : imageHeight;
  }

  return pinImageHeight;
};

// Related KLP Story Pin Utils
export const getRelatedKlpStoryMeasurements = () => {
  const relatedKlpPillHeight = 38;
  const pillMargin = 12;
  const storyTitleHeight = 17;
  const storyTitleBottomMargin = 18;
  const maxRelatedInterests = 6;

  return {
    relatedKlpPillHeight,
    pillMargin,
    storyTitleHeight,
    storyTitleBottomMargin,
    maxRelatedInterests,
  };
};

export const calculateRelatedKlpStoryHeight = (numRelatedInterests: number) => {
  const storyMeasurements = getRelatedKlpStoryMeasurements();
  const numInterestsToShow = Math.min(numRelatedInterests, storyMeasurements.maxRelatedInterests);
  return (
    numInterestsToShow * storyMeasurements.relatedKlpPillHeight +
    (numInterestsToShow - 1) * storyMeasurements.pillMargin +
    storyMeasurements.storyTitleHeight +
    storyMeasurements.storyTitleBottomMargin
  );
};

// Board Info Card Pin Utils
export const getBoardInfoCardMeasurements = () => {
  const boardTitleHeight = 80;
  const boardDataHeight = 16;
  const boardDescriptionHeight = 32;
  const pinnerAttributionHeight = 40;
  const relatedKlpPillHeight = 42;
  const marginTopHeight = 16;

  return {
    boardTitleHeight,
    boardDataHeight,
    boardDescriptionHeight,
    pinnerAttributionHeight,
    relatedKlpPillHeight,
    marginTopHeight,
  };
};

// Response limited non-duplicated link pins
export const filterDuplicateLinkPins = (
  data: Array<PinType>,
  pinNumber: number = SIMILAR_PINS_NUMBER,
) => {
  const pins = [];
  if (data.length > 0) {
    const pinLinkSet = new Set();
    let index = 0;
    while (pins.length < pinNumber && index < data.length) {
      const candidatePin = data[index];
      if (
        candidatePin.images &&
        (!candidatePin.link || (candidatePin.link && !pinLinkSet.has(candidatePin.link)))
      ) {
        pins.push(candidatePin);
        pinLinkSet.add(candidatePin.link);
      }
      index += 1;
    }
  }

  return pins;
};

export default null;
